/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { TextViewProps } from '@airasia-phoenix/react-native/components/TextView';

import { EmailFieldProps } from '@airasia-phoenix/react-native/components/EmailFieldView';

import {
  ButtonViewProps,
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { PasswordFieldViewProps } from '@airasia-phoenix/react-native/components/PasswordFieldView';

import { IconViewProps } from '@airasia-phoenix/react-native/components/IconView';
import { pd } from '@airasia-phoenix/react-native/utils';
import {
  getColor,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
// Add these imports at the top
import { MobileFieldViewProps } from '@airasia-phoenix/react-native/components/MobileFieldView';
import { SelectionFieldViewProps } from '@airasia-phoenix/react-native/components/SelectionFieldView';
import {
  AccessTokenResponse,
  MobileLoginResponse,
  PasswordlessResponse,
  SocialAuthResponse,
} from '../../managers';
import type { TwoFaLoginData } from '../../hooks/useEmailLoginSubmit';
import type { SocialIncompleteAuthData } from '../../utils/googleAuthExchange';

export type MobileLoginType = {
  mobileNumber: string;
  dialingCode: string;
};

/** Top-level banner shown above the form. Shared by the email, mobile, social and passkey flows. */
export type LoginNotification = {
  message: string;
  type: NotificationViewStyle;
};

/** Passwordless code requested; `cancelDeletion` after keep-account Yes, so the OTP step sends it too. */
export type PasswordlessLoginResult = PasswordlessResponse & {
  cancelDeletion?: boolean;
};

export type MobileLoginErrorsType = {
  mobileNumberError: boolean;
};

export const MobileLoginViewData = {
  mobileNumberLabel: 'Mobile Number',
  dialingCodeLabel: 'Dial Code',
};

// Mobile Login Data Type
export type MobileLoginData = {
  mobileNumber: string;
  dialingCode: string;
};

export enum EmailLoginMode {
  PASSWORD = 'password',
  PASSWORDLESS = 'passwordless',
}

// Login method types
export type LoginMethod =
  | 'google'
  | 'facebook'
  | 'apple'
  | 'wechat'
  | 'email'
  | 'mobile'
  | 'passkey';

// Google Login Data Type
export type GoogleLoginData = {
  method?: 'google';
  user: {
    id: string;
    name: string | null;
    email: string | null;
    photo: string | null;
  };
  idToken: string | null;
  serverAuthCode: string | null;
  googleClientId: string | null;
};

// Facebook Login Data Type
export type FacebookLoginData = {
  method?: 'facebook';
  user: {
    id: string;
    name: string | null;
    email: string | null;
    photo: string | null;
  };
  accessToken: string | null;
};

// Email Login Data Type
export type EmailLoginData = {
  email: string;
  password: string;
};

// Passkey Login Data Type
export type PasskeyLoginData = {
  method: 'passkey';
  userName?: string;
};

// Union type for all login data types
export type LoginData =
  | GoogleLoginData
  | FacebookLoginData
  | EmailLoginData
  | MobileLoginData
  | SocialAuthResponse
  | AppleLoginData
  | PasskeyLoginData;

/**
 * What the login sheets pass to `onLoginSuccess`: the login form's data, or
 * the session from a verified OTP step (mobile/email code).
 */
export type SheetLoginSuccessData = LoginData | AccessTokenResponse | undefined;

// authenticated = session; incomplete = 202 (no session yet).
export type SocialLoginResult =
  | { kind: 'authenticated'; data: LoginData }
  | { kind: 'incomplete'; data: SocialIncompleteAuthData };

// Apple Login Data Type
export type AppleLoginData = {
  method?: 'apple';
  user: {
    id: string; // appleId
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  authCode: string | null; // authorization code from Apple
  identityToken: string | null; // identity token from Apple
};

// Login Error Type
export type LoginError = {
  method: LoginMethod;
  error: Error | string;
};

// Google Token Validation Result
export type GoogleTokenValidationResult = {
  isValid: boolean;
  error?: string;
};

// Facebook Token Validation Result
export type FacebookTokenValidationResult = {
  isValid: boolean;
  error?: string;
};

// Apple Token Validation Result
export type AppleTokenValidationResult = {
  isValid: boolean;
  error?: string;
};

// Google Sign-In Callback Interface for platform-specific implementations
export type onGoogleLoginRequest = () => Promise<GoogleLoginData>;

// Facebook Sign-In Callback Interface for platform-specific implementations
export type onFacebookLoginRequest = () => Promise<FacebookLoginData>;

// Apple Sign-In Callback Interface for platform-specific implementations
export type onAppleLoginRequest = () => Promise<AppleLoginData>;

export type LoginPageProps = {
  onCloseIconClick?: () => void;
  onForgotPasswordClick?: () => void;
  onSignUpClick?: () => void;
  onLanguageClick?: () => void;
  headings?: {
    closeIcon: IconViewProps;
    greeting: TextViewProps;
    prompt: TextViewProps;
    action: TextViewProps;
    forgotPassword: TextViewProps;
    dividerText: TextViewProps;
    continueAsGuestProps: TextViewProps;
  };
  emailFieldProps?: EmailFieldProps;
  passwordFieldProps?: PasswordFieldViewProps;
  mobileFieldProps?: MobileFieldViewProps;
  dialingCodeFieldProps?: SelectionFieldViewProps;
  primaryButtonProps?: ButtonViewProps;
  loginWithMobileNumber?: ButtonViewProps;
  loginWithPasskey?: ButtonViewProps;
  socialLogins?: {
    facebook: { icon: IconViewProps; label: TextViewProps };
    google: { icon: IconViewProps; label: TextViewProps };
    apple?: { icon: IconViewProps; label: TextViewProps };
    wechat: { icon: IconViewProps; label: TextViewProps };
  };
  disclaimer?: TextViewProps & {
    spannableTextsProps?: TextViewProps['spannableTextsProps'];
  };
  onPasswordLessEmailLoginSuccess?: (data: PasswordlessLoginResult) => void;
  onTwoFactorRequired?: (data: TwoFaLoginData) => void;
  onPasswordLessEmailLoginError?: (errorData: LoginError) => void;
  /**
   * Awaited: the sign-in tail (profile check, auth handoff, navigation) runs
   * inside it, and the login loader stays up until it resolves.
   */
  onLoginSuccess?: (data: LoginData) => void | Promise<void>;
  onSocialLoginSuccess?: (result: SocialLoginResult) => void | Promise<void>;
  onLoginError?: (errorData: LoginError) => void;
  onLoadingChange?: (loading: boolean) => void;
  onEmailLoginPress?: (data: { email: string; password: string }) => void;
  onMobileLoginPress?: (data: MobileLoginType) => void;
  onMobileLoginSuccess?: (data: MobileLoginResponse) => void;
  onGoogleLoginRequest?: onGoogleLoginRequest;
  onFacebookLoginRequest?: onFacebookLoginRequest;
  onAppleLoginRequest?: onAppleLoginRequest;
  emailLoginMode?: EmailLoginMode;
  prefilledEmail?: string;
  /** Which form the screen opens on. Only `'mobile'` switches away from the email form. */
  initialLoginMethod?: LoginMethod;
  onEmailDomainDropdownChange?: (isOpen: boolean, dismiss: () => void) => void;
};

export const defaultProps: LoginPageProps = {
  headings: {
    closeIcon: {
      icon: pd.icon.main.navigation.close,
      height: 24,
      width: 24,
      tint: getColor(pd.sys.colour.content_primary),
      testID: 'close-icon',
    },
    greeting: {
      text: 'Welcome back',
      style: getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.content_primary,
      ),
    },
    prompt: {
      text: "Don't have an account? ",
      style: getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.content_secondary,
      ),
    },
    action: {
      text: 'Sign up',
      style: getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.interactive_link,
      ),
    },
    forgotPassword: {
      text: 'Forgot password',
      style: getTextStyle(
        pd.sys.typescale.sub_body_text_medium,
        pd.sys.colour.interactive_link,
      ),
    },
    dividerText: {
      text: 'Or',
      style: [
        getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_secondary,
        ),
      ],
    },
    continueAsGuestProps: {
      text: 'Continue as guest',
      style: getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.interactive_link,
      ),
    },
  },
  emailFieldProps: {
    label: 'Email',
    disabled: false,
    value: '',
    isError: false,
    errorMessage: 'Please enter a valid email',
    suggestions: [],
    onError: () => {},
  },
  passwordFieldProps: {
    label: 'Password',
    disabled: false,
    value: '',
    isError: false,
    maxLength: 16,
    errorMessage: 'Not a valid password',
    onChangeText: () => {},
    onError: () => {},
  },
  primaryButtonProps: {
    text: 'Log in',
    type: ButtonViewType.PRIMARY_REGULAR,
    disabled: false,
    testID: 'submit-button',
  },
  loginWithMobileNumber: {
    text: 'Log in with Mobile number',
    type: ButtonViewType.SECONDARY_REGULAR,
    disabled: false,
    startIcon: {
      url: 'https://static.airasia.com/mobile/phoenix/icons/v1/main/svg/hardware/phone_iphone.svg',
      tint: getColor(pd.sys.colour.secondary),
    },
  },
  loginWithPasskey: {
    text: 'Log in with Passkey',
    type: ButtonViewType.SECONDARY_REGULAR,
    disabled: false,
    startIcon: { icon: pd.icon.main.communication.vpn_key },
  },
  socialLogins: {
    facebook: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/facebook.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => {
          // Facebook login not implemented
        },
        testID: 'facebook-icon',
      },
      label: {
        text: 'Facebook',
        style: getTextStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'facebook-text',
      },
    },

    google: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/google.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => console.log('Google pressed'),
        testID: 'google-icon',
      },
      label: {
        text: 'Google',
        style: getTextStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'google-text',
      },
    },

    apple: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/apple.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => {},
        testID: 'apple-icon',
      },
      label: {
        text: 'Apple',
        style: getTextStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'apple-text',
      },
    },

    wechat: {
      icon: {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        source: require('../../assets/wechat.png'),
        height: 45,
        width: 45,
        tint: getColor(pd.sys.colour.content_primary),
        onPress: () => {
          // WeChat login not implemented
        },
        testID: 'wechat-icon',
      },
      label: {
        text: 'WeChat',
        style: getTextStyle(
          pd.sys.typescale.sub_body_text,
          pd.sys.colour.content_secondary,
        ),
        testID: 'wechat-text',
      },
    },
  },

  disclaimer: {
    text: "This platform is protected by reCAPTCHA. By logging in, you are deemed to have read and agreed to (i) AirAsia MOVE's Terms and conditions, Terms of Use and Privacy Statement; and (ii) Google's Terms of Service and Privacy Policy.",
    style: getTextStyle(
      pd.sys.typescale.caption_text,
      pd.sys.colour.content_secondary,
    ),
    spannableTextsProps: [
      {
        spannable: 'Terms and conditions',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
      },
      {
        spannable: 'Terms of Use',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
      },
      {
        spannable: 'Privacy Statement',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
      },
      {
        spannable: 'Terms of Service',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
      },
      {
        spannable: 'Privacy Policy',
        highlightColor: pd.sys.colour.interactive_link.hex,
        style: getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.interactive_link,
        ),
      },
    ],
  },
  onEmailLoginPress: () => {},
  onMobileLoginPress: () => {},
  onMobileLoginSuccess: () => {},
  onGoogleLoginRequest: undefined,
  onFacebookLoginRequest: undefined,
  onAppleLoginRequest: undefined,
  mobileFieldProps: {
    label: 'Mobile Number',
    disabled: false,
    value: '',
    isError: false,
    errorMessage: '',
    dialingCodeFieldViewProps: {
      label: 'Dial Code',
      value: '+60',
      disabled: false,
      isError: false,
      errorMessage: '',
      searchableListViewProps: {
        isInsideBottomSheetView: true,
        searchableListPageViewData: [],
        onChange: () => {},
        isTickList: false,
      },
    },
  },
  dialingCodeFieldProps: {
    label: 'Dial Code',
    value: '+60',
    disabled: false,
    isError: false,
    errorMessage: '',
    searchableListViewProps: {
      isInsideBottomSheetView: true,
      searchableListPageViewData: [],
      onChange: () => {},
      isTickList: false,
    },
  },
};
